import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import Logo from './Logo';
import type { ViewType } from '../data/homepageData';
import { useAuth } from '@/hooks/useAuth';

interface NavBarProps {
  view: ViewType;
}

/**
 * Fixed navigation bar with scroll-detection glass effect.
 * Shows auth links that adapt to the current view (creator | brand).
 */
const NavBar: React.FC<NavBarProps> = ({ view }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const { user, profile, loading } = useAuth();
  const isSignedIn = !!user && !!profile && !loading;

  const authLink = view === 'creator' ? '/auth?type=creator' : '/auth?type=brand';
  const ctaLabel = view === 'creator' ? 'Start Earning' : 'Launch Campaign';

  return (
    <nav
      className={cn(
        'cv-nav fixed top-0 left-0 right-0 z-[100] py-4',
        scrolled && 'cv-nav--scrolled',
      )}
    >
      <div className="flex items-center justify-between max-w-[1200px] mx-auto px-6 md:px-4">
        {/* Logo */}
        <a
          href="/"
          className="flex items-center gap-2.5 font-display font-extrabold text-[20px] tracking-tight no-underline"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          <Logo size={28} />
          <span>Creator</span>
          <span className="cv-text-gradient">Vault</span>
        </a>

        {/* Desktop nav links */}
        <div className="cv-nav-links-desktop hidden md:flex items-center gap-8">
          {[
            { label: 'Features', href: '#features' },
            { label: 'Platform', href: '#platform' },
            { label: 'Pricing', href: '/pricing', external: true },
            { label: 'FAQ', href: '#faq' },
          ].map(({ label, href, external }) => (
            <a
              key={label}
              href={href}
              {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className="text-sm font-medium transition-colors"
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--cv-text-muted)',
              }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLAnchorElement).style.color = 'var(--cv-text)')}
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLAnchorElement).style.color = 'var(--cv-text-muted)')
              }
            >
              {label}
            </a>
          ))}
        </div>

        {/* CTA buttons */}
        <div className="flex items-center gap-4">
          {isSignedIn ? (
            /* Signed-in: single Dashboard CTA */
            <a
              href="/dashboard"
              className="cv-btn-primary"
              style={{ padding: '10px 22px', fontSize: '14px' }}
            >
              Dashboard
            </a>
          ) : (
            <>
              {/* Ghost login — hidden on mobile */}
              <a href={authLink} className="cv-btn-ghost cv-nav-ghost-desktop hidden md:inline-flex">
                Log in
              </a>

              {/* Primary CTA */}
              <a
                href={authLink}
                className="cv-btn-primary"
                style={{ padding: '10px 22px', fontSize: '14px' }}
              >
                {ctaLabel}
              </a>
            </>
          )}

          {/* Mobile hamburger */}
          <button
            className="cv-mobile-menu-btn md:hidden flex items-center justify-center w-10 h-10"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle navigation menu"
            aria-expanded={mobileOpen}
          >
            <svg
              width={24}
              height={24}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path d="M3 12h18M3 6h18M3 18h18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu (basic dropdown) */}
      {mobileOpen && (
        <div
          className="md:hidden border-t px-6 py-3 flex flex-col"
          style={{
            background: 'rgba(7, 6, 11, 0.98)',
            borderColor: 'var(--cv-border)',
            paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))',
          }}
        >
          {[
            { label: 'Features', href: '#features' },
            { label: 'Platform', href: '#platform' },
            { label: 'Pricing', href: '/pricing', external: true },
            { label: 'FAQ', href: '#faq' },
          ].map(({ label, href, external }) => (
            <a
              key={label}
              href={href}
              {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className="font-medium flex items-center"
              style={{ color: 'var(--cv-text-muted)', fontSize: '15px', minHeight: 48 }}
              onClick={() => setMobileOpen(false)}
            >
              {label}
            </a>
          ))}
          <a
            href={isSignedIn ? '/dashboard' : authLink}
            className="font-medium flex items-center"
            style={{ color: 'var(--cv-text-muted)', fontSize: '15px', minHeight: 48 }}
            onClick={() => setMobileOpen(false)}
          >
            {isSignedIn ? 'Dashboard' : 'Log in'}
          </a>
        </div>
      )}
    </nav>
  );
};

export default NavBar;
